import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Users,
  Plus,
  Edit,
  Trash2,
  Eye,
  Search,
  Mail,
  Phone,
  BookOpen,
  Award,
  Calendar,
  RefreshCw,
  Download,
  Filter,
  UserCheck,
  UserX,
  MapPin,
} from "lucide-react";
import axios from "axios";
import { server } from "../server";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

const TeachersPage = () => {
  const navigate = useNavigate();
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState(null);

  useEffect(() => {
    fetchTeachers();
  }, []);

  const fetchTeachers = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${server}/teacher/all`, {
        params: {
          pageNo: 1,
          pageSize: 10,
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      if (data.success && data.data) {
        // Map API response to match UI requirements
        const mappedTeachers = data.data.map((teacher) => ({
          ...teacher,
          subjects: teacher.selectedSubjects || [],
          totalBatches: teacher.totalBatches || 0,
          totalStudents: teacher.totalStudents || 0,
        }));
        setTeachers(mappedTeachers);
      }
    } catch (error) {
      toast.error("Failed to fetch teachers");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (teacherId) => {
    if (!window.confirm("Are you sure you want to remove this teacher?")) {
      return;
    }

    try {
      await axios.delete(`${server}/teacher/deleteById`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        params: {
          teacherId: teacherId,
        },
      });
      toast.success("Teacher removed successfully");
      fetchTeachers();
    } catch (error) {
      toast.error("Failed to remove teacher");
    }
  };

  const handleStatusToggle = async (teacherId, currentStatus) => {
    const newStatus = currentStatus === "active" ? "inactive" : "active";
    try {
      await axios.patch(
        `${server}/teachers/${teacherId}/status`,
        { status: newStatus },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      toast.success(
        `Teacher ${newStatus === "active" ? "activated" : "deactivated"}`
      );
      fetchTeachers();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const filteredTeachers = teachers.filter((teacher) => {
    const matchesSearch =
      `${teacher.name} ${teacher.email} ${teacher.specialization}`
        .toLowerCase()
        .includes(searchQuery.toLowerCase());
    const matchesStatus =
      filterStatus === "all" || teacher.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-6">
      <motion.div
        className="max-w-7xl mx-auto space-y-6"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2 flex items-center gap-3">
              <div className="p-3 bg-gradient-to-br from-orange-600 to-red-600 rounded-2xl">
                <Users className="w-8 h-8 text-white" />
              </div>
              Teachers Management
            </h1>
            <p className="text-gray-400 text-lg">
              Manage faculty and teaching staff
            </p>
          </div>

          <div className="flex gap-3">
            <button className="flex items-center gap-2 px-4 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors border border-gray-700">
              <Download className="w-4 h-4" />
              Export
            </button>
            <button
              onClick={() => navigate("/my/registerTeacher")}
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-br from-orange-600 to-red-600 text-white rounded-lg hover:shadow-lg hover:shadow-orange-500/20 transition-all font-semibold"
            >
              <Plus className="w-5 h-5" />
              Add Teacher
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name, email, or specialization..."
                className="w-full pl-10 pr-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-orange-500 transition-colors"
              />
            </div>

            {/* Status Filter */}
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-orange-500 transition-colors"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <StatCard
            title="Total Teachers"
            value={teachers.length}
            icon={Users}
            color="text-orange-400"
            bg="bg-orange-500/20"
          />
          <StatCard
            title="Active"
            value={teachers.filter((t) => t.status === "active").length}
            icon={UserCheck}
            color="text-green-400"
            bg="bg-green-500/20"
          />
          <StatCard
            title="Total Students"
            value={teachers.reduce((sum, t) => sum + t.totalStudents, 0)}
            icon={Award}
            color="text-blue-400"
            bg="bg-blue-500/20"
          />
          <StatCard
            title="Total Batches"
            value={teachers.reduce((sum, t) => sum + t.totalBatches, 0)}
            icon={BookOpen}
            color="text-purple-400"
            bg="bg-purple-500/20"
          />
        </div>

        {/* Teachers Table */}
        <div className="bg-gray-800 rounded-2xl border border-gray-700 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <RefreshCw className="w-8 h-8 text-orange-400 animate-spin" />
            </div>
          ) : filteredTeachers.length === 0 ? (
            <div className="text-center py-20">
              <Users className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 text-lg">No teachers found</p>
              <button
                onClick={() => navigate("/my/registerTeacher")}
                className="mt-4 px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
              >
                Add First Teacher
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-900 border-b border-gray-700">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase">
                      Teacher
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase">
                      Contact
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase">
                      Specialization
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase">
                      Experience
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase">
                      Students/Batches
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase">
                      Status
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {filteredTeachers?.map((teacher) => (
                    <tr
                      key={teacher?.id}
                      className="hover:bg-gray-900/50 transition-colors"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                            {teacher?.name
                              ?.split(" ")
                              ?.map((n) => n[0])
                              ?.join("")}
                          </div>
                          <div>
                            <div className="text-white font-medium">
                              {teacher?.name}
                            </div>
                            <div className="text-gray-400 text-sm">
                              {teacher?.qualification}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2 text-gray-300 text-sm">
                            <Mail className="w-4 h-4 text-gray-400" />
                            {teacher?.email}
                          </div>
                          <div className="flex items-center gap-2 text-gray-300 text-sm">
                            <Phone className="w-4 h-4 text-gray-400" />
                            {teacher?.phone}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-white font-medium">
                            {teacher?.specialization}
                          </div>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {teacher?.subjects?.map((subject, idx) => (
                              <span
                                key={idx}
                                className="px-2 py-1 bg-orange-500/20 text-orange-400 rounded text-xs"
                              >
                                {typeof subject === "object"
                                  ? subject?.subject
                                  : subject}
                              </span>
                            ))}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-gray-300">
                        {teacher?.experience} years
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm">
                          <div className="text-white">
                            {teacher?.totalStudents || 0} students
                          </div>
                          <div className="text-gray-400">
                            {teacher?.totalBatches || 0} batches
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() =>
                            handleStatusToggle(teacher?.id, teacher?.status)
                          }
                          className={`px-3 py-1 rounded-full text-xs font-medium border ${
                            teacher?.status === "active"
                              ? "bg-green-500/20 text-green-400 border-green-500"
                              : "bg-red-500/20 text-red-400 border-red-500"
                          }`}
                        >
                          {teacher?.status}
                        </button>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              setSelectedTeacher(teacher);
                              setShowDetailsModal(true);
                            }}
                            className="p-2 text-blue-400 hover:bg-blue-500/10 rounded-lg transition-colors"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() =>
                              navigate(`/my/editTeacher/${teacher?.id}`, {
                                state: { teacher },
                              })
                            }
                            className="p-2 text-green-400 hover:bg-green-500/10 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(teacher?.id)}
                            className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </motion.div>

      {/* Details Modal */}
      {showDetailsModal && selectedTeacher && (
        <TeacherDetailsModal
          teacher={selectedTeacher}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedTeacher(null);
          }}
        />
      )}
    </div>
  );
};

// Stat Card Component
const StatCard = ({ title, value, icon: Icon, color, bg }) => {
  return (
    <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
      <div className="flex items-center gap-3 mb-2">
        <div className={`p-2 ${bg} rounded-lg`}>
          <Icon className={`w-5 h-5 ${color}`} />
        </div>
        <span className="text-gray-400 text-sm">{title}</span>
      </div>
      <p className="text-3xl font-bold text-white">{value}</p>
    </div>
  );
};

// Teacher Details Modal
const TeacherDetailsModal = ({ teacher, onClose }) => {
  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-gray-800 rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-700"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-white">Teacher Details</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
          >
            <span className="text-gray-400 text-2xl">×</span>
          </button>
        </div>

        <div className="space-y-6">
          {/* Profile */}
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-red-500 rounded-full flex items-center justify-center text-white font-bold text-2xl">
              {teacher.name
                .split(" ")
                .map((n) => n[0])
                .join("")}
            </div>
            <div>
              <h3 className="text-2xl font-bold text-white">{teacher.name}</h3>
              <p className="text-gray-400">{teacher.qualification}</p>
            </div>
          </div>

          {/* Information Grid */}
          <div className="grid grid-cols-2 gap-4">
            <InfoItem label="Email" value={teacher.email} icon={Mail} />
            <InfoItem
              label="Phone"
              value={teacher.phone || teacher.phoneNo}
              icon={Phone}
            />
            <InfoItem
              label="Specialization"
              value={teacher.specialization}
              icon={BookOpen}
            />
            <InfoItem
              label="Experience"
              value={`${teacher.experience} years`}
              icon={Award}
            />
            <InfoItem
              label="Qualification"
              value={teacher.qualification}
              icon={Award}
            />
            <InfoItem
              label="Joining Date"
              value={
                teacher.joiningDate
                  ? new Date(teacher.joiningDate).toLocaleDateString()
                  : "N/A"
              }
              icon={Calendar}
            />
            <InfoItem
              label="Date of Birth"
              value={
                teacher.dateOfBirth
                  ? new Date(teacher.dateOfBirth).toLocaleDateString()
                  : "N/A"
              }
              icon={Calendar}
            />
            <InfoItem label="Status" value={teacher.status} icon={UserCheck} />
            <InfoItem
              label="Employment"
              value={teacher.employmentType || "full_time"}
              icon={UserCheck}
            />
            <InfoItem
              label="Salary"
              value={`₹${teacher.salary}`}
              icon={Award}
            />
            <InfoItem
              label="Gender"
              value={teacher.gender || "male"}
              icon={UserCheck}
            />
            <InfoItem label="City" value={teacher.city || "-"} icon={MapPin} />
            <InfoItem
              label="State"
              value={teacher.state || "-"}
              icon={MapPin}
            />
            <InfoItem
              label="Pincode"
              value={teacher.pincode || "-"}
              icon={MapPin}
            />
          </div>

          {/* Subjects */}
          <div>
            <h4 className="text-white font-semibold mb-2">Subjects Teaching</h4>
            <div className="flex flex-wrap gap-2">
              {teacher?.subjects?.map((subject, idx) => (
                <span
                  key={idx}
                  className="px-3 py-1 bg-orange-500/20 text-orange-400 rounded-lg"
                >
                  {typeof subject === "object" ? subject?.subject : subject}
                </span>
              ))}
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-900 rounded-lg p-4">
              <p className="text-gray-400 text-sm mb-1">Total Students</p>
              <p className="text-2xl font-bold text-white">
                {teacher.totalStudents}
              </p>
            </div>
            <div className="bg-gray-900 rounded-lg p-4">
              <p className="text-gray-400 text-sm mb-1">Total Batches</p>
              <p className="text-2xl font-bold text-white">
                {teacher.totalBatches}
              </p>
            </div>
          </div>

          {/* Address */}
          {teacher.address && (
            <div>
              <h4 className="text-white font-semibold mb-2">Address</h4>
              <p className="text-gray-300">{teacher.address}</p>
            </div>
          )}

          {/* Previous Institute */}
          {teacher.previousInstitute && (
            <div>
              <h4 className="text-white font-semibold mb-2">
                Previous Institute
              </h4>
              <p className="text-gray-300">{teacher.previousInstitute}</p>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

// Info Item Component
const InfoItem = ({ label, value, icon: Icon }) => {
  return (
    <div className="bg-gray-900 rounded-lg p-3">
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-4 h-4 text-gray-400" />
        <span className="text-gray-400 text-sm">{label}</span>
      </div>
      <p className="text-white font-medium">{value}</p>
    </div>
  );
};

export default TeachersPage;
